import type { PublicAvailabilitySlot } from "@emr/contracts";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { Icon } from "../Icon";
import { formatLocalDate, sameClockAsDevice } from "../lib/appointmentTime";
import { RequiredMarker } from "../RequiredMarker";
import { addDays, formatMonth, monthOf, timeZoneCity } from "./availabilityCalendar";
import { type CalendarDayState, MonthCalendar } from "./MonthCalendar";
import { scrollBehavior } from "./motion";
import { namedPhysicians, SlotChoices, sortSlots } from "./SlotChoices";
import type { CalendarAvailability } from "./useAvailabilityCalendar";

/** Whether every day from `from` through `to` has an answer. */
function allAnswered(from: string, to: string, answered: (date: string) => boolean): boolean {
  for (let day = from; day <= to; day = addDays(day, 1)) {
    if (!answered(day)) return false;
  }
  return true;
}

/**
 * The one date and time picker (booking and a time change): a month calendar beside the chosen
 * day's times, two columns on a wide screen and the calendar above the times on a phone. Days
 * with times are marked from the month's searches (useAvailabilityCalendar). With no day chosen
 * yet it picks one: `preferredDate` when it has times, else the first day with times in the
 * month on screen, else the first day with times at all (and shows that day's month).
 */
export function DateTimePicker({
  headingLevel,
  availability,
  month,
  onMonthChange,
  today,
  firstDay,
  lastDay,
  date,
  onDate,
  preferredDate,
  slot,
  onSlot,
  timeZone,
  physicianName,
  emptyHint,
  noTimes,
  name,
}: {
  /** The level of the "Select date" heading under the step's own heading. */
  readonly headingLevel: 2 | 3;
  readonly availability: CalendarAvailability;
  readonly month: string;
  readonly onMonthChange: (month: string) => void;
  /** Today, and the first and last bookable days, on the clinic's calendar. */
  readonly today: string;
  readonly firstDay: string;
  readonly lastDay: string;
  /** The chosen day, or "" for none yet. */
  readonly date: string;
  readonly onDate: (date: string) => void;
  readonly preferredDate?: string;
  readonly slot: PublicAvailabilitySlot | null;
  readonly onSlot: (slot: PublicAvailabilitySlot) => void;
  /** The clinic's time zone, until a search names it. */
  readonly timeZone: string;
  readonly physicianName: (providerId: string) => string;
  /** What to try when the chosen day has no times. */
  readonly emptyHint: string;
  /** What to do when no day from firstDay to lastDay has times, under "No online times right now." */
  readonly noTimes?: ReactNode;
  /** The time radios' group name. */
  readonly name: string;
}) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const { days, answered, unread, pending, failed } = availability;
  const zone = availability.timeZone ?? timeZone;
  const calendarPanel = useRef<HTMLDivElement>(null);
  const timesPanel = useRef<HTMLDivElement>(null);
  const openDays = useMemo(
    () =>
      [...days.keys()]
        .filter((day) => day >= firstDay && day <= lastDay && (days.get(day)?.length ?? 0) > 0)
        .sort(),
    [days, firstDay, lastDay],
  );

  useEffect(() => {
    if (date !== "" || pending || failed) return;
    const pick =
      (preferredDate !== undefined && openDays.includes(preferredDate) ? preferredDate : null) ??
      openDays.find((day) => monthOf(day) === month) ??
      openDays[0];
    if (pick === undefined) return;
    // The calendar always shows the chosen day.
    if (monthOf(pick) !== month) onMonthChange(monthOf(pick));
    onDate(pick);
  }, [date, pending, failed, openDays, preferredDate, month, onMonthChange, onDate]);

  const chooseDay = (day: string, pointer: boolean) => {
    onDate(day);
    // A tap on a phone, where the times sit under the calendar: bring them into view. Not for
    // the keyboard, whose focus stays on the calendar.
    const times = timesPanel.current;
    const calendar = calendarPanel.current;
    if (!pointer || times === null || calendar === null) return;
    if (typeof times.scrollIntoView !== "function") return;
    const top = times.getBoundingClientRect().top;
    const stacked = top >= calendar.getBoundingClientRect().bottom;
    if (stacked && top > window.innerHeight / 2) {
      times.scrollIntoView({ block: "start", behavior: scrollBehavior() });
    }
  };

  const dayState = (day: string): CalendarDayState => {
    if (day < firstDay || day > lastDay) return "closed";
    if ((days.get(day)?.length ?? 0) > 0) return "open";
    if (answered(day)) return "none";
    return failed || unread(day) ? "unknown" : "loading";
  };
  const daySlots = sortSlots(date === "" ? [] : (days.get(date) ?? []), physicianName);
  const physicians = namedPhysicians(daySlots);
  const differentClock = daySlots[0] !== undefined && !sameClockAsDevice(daySlots[0].startAt, zone);

  let times: ReactNode;
  if (failed) {
    times = (
      <div className="pa-times-state" role="alert">
        <p>
          <strong>Times could not be loaded.</strong>
        </p>
        <button type="button" className="outline-button" onClick={availability.refetch}>
          Try again
        </button>
      </div>
    );
  } else if (date !== "" && unread(date)) {
    times = <p className="pa-times-state">This day's times could not be loaded. {emptyHint}</p>;
  } else if (date === "" ? pending : !answered(date)) {
    times = (
      <p className="pa-times-state" role="status">
        <span className="button-spinner" />
        Finding times…
      </p>
    );
  } else if (date === "") {
    // Nothing anywhere in the window: another month cannot help.
    times =
      openDays.length === 0 && allAnswered(firstDay, lastDay, answered) ? (
        <div className="pa-times-state">
          <p>No online times right now.</p>
          {noTimes}
        </div>
      ) : (
        <p className="pa-times-state">No times in {formatMonth(month)}. Try another month.</p>
      );
  } else if (daySlots.length === 0) {
    times = <p className="pa-times-state">No times on this day. {emptyHint}</p>;
  } else {
    times = (
      <SlotChoices
        slots={daySlots}
        timeZone={zone}
        selected={slot}
        select={onSlot}
        physicianName={physicianName}
        name={name}
      />
    );
  }

  return (
    <div className="pa-picker">
      <div className="pa-picker-panel" ref={calendarPanel}>
        <Heading className="pa-picker-title">Select date</Heading>
        <MonthCalendar
          month={month}
          onMonthChange={onMonthChange}
          minMonth={monthOf(firstDay)}
          maxMonth={monthOf(lastDay)}
          today={today}
          selected={date}
          onSelect={chooseDay}
          dayState={dayState}
        />
      </div>
      {/* The panel is a plain box: a bordered fieldset would draw its legend on the border. */}
      <div className="pa-picker-panel" ref={timesPanel}>
        <fieldset className="pa-times">
          <legend className="pa-picker-title">
            Select time
            {daySlots.length > 0 ? <RequiredMarker /> : null}
          </legend>
          {date === "" ? null : (
            <p className="pa-times-date">
              <Icon name="calendar" />
              {formatLocalDate(date)}
              {differentClock ? <span> · {timeZoneCity(zone)} time</span> : null}
            </p>
          )}
          {physicians.length === 1 && physicians[0] !== undefined ? (
            <p className="pa-times-who">With {physicianName(physicians[0])}</p>
          ) : null}
          {times}
        </fieldset>
      </div>
    </div>
  );
}
